/**
 * Magic Link Client Wrapper
 * Handles authentication and wallet operations
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class MagicClient {
    constructor(config) {
        this.magic = null;
        this.initialized = false;
        this.config = config;
    }
    /**
     * Initialize Magic SDK
     * Loads the Magic SDK from CDN if not already loaded
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.initialized) {
                return;
            }
            // Load Magic SDK from CDN if not already loaded
            if (!window.Magic) {
                yield this.loadMagicSDK();
            }
            // Initialize Magic instance with network configuration
            this.magic = new window.Magic(this.config.apiKey, {
                network: {
                    rpcUrl: this.config.rpcUrl,
                    chainId: parseInt(this.config.chainId),
                },
            });
            this.initialized = true;
        });
    }
    /**
     * Load Magic SDK from CDN
     */
    loadMagicSDK() {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.Magic) {
                resolve();
                return;
            }
            const script = document.createElement("script");
            script.src = "https://auth.magic.link/sdk";
            script.async = true;
            script.onload = () => {
                // Wait a bit for Magic to be available
                setTimeout(() => resolve(), 100);
            };
            script.onerror = () => reject(new Error("Failed to load Magic SDK"));
            document.head.appendChild(script);
        });
    }
    /**
     * Check if user is logged in
     */
    isLoggedIn() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initialized) {
                yield this.init();
            }
            try {
                return yield this.magic.user.isLoggedIn();
            }
            catch (error) {
                console.error("Error checking Magic login status:", error);
                return false;
            }
        });
    }
    /**
     * Get user metadata (including wallet address)
     */
    getUserMetadata() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.initialized) {
                yield this.init();
            }
            try {
                // Magic SDK uses getInfo() method, not getMetadata()
                const metadata = yield this.magic.user.getInfo();
                // Extract wallet address from the wallets object
                const publicAddress = (_b = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.wallets) === null || _a === void 0 ? void 0 : _a.ethereum) === null || _b === void 0 ? void 0 : _b.publicAddress;
                if (!publicAddress) {
                    console.error("Failed to extract wallet address from metadata:", metadata);
                    throw new Error("Magic returned invalid user metadata - no Ethereum wallet address found");
                }
                return {
                    publicAddress,
                    email: metadata.email || null,
                };
            }
            catch (error) {
                console.error("Error getting Magic user metadata:", error);
                throw error;
            }
        });
    }
    /**
     * Login with email OTP
     */
    loginWithEmailOTP(email) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initialized) {
                yield this.init();
            }
            try {
                // Magic SDK v11+ uses loginWithEmailOTP directly on auth
                const didToken = yield this.magic.auth.loginWithEmailOTP({
                    email,
                    showUI: true, // Show Magic's UI for OTP entry
                });
                return didToken;
            }
            catch (error) {
                console.error("Error logging in with Magic:", error);
                throw error;
            }
        });
    }
    /**
     * Logout
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initialized) {
                return;
            }
            try {
                yield this.magic.user.logout();
            }
            catch (error) {
                console.error("Error logging out from Magic:", error);
                throw error;
            }
        });
    }
    /**
     * Get wallet address (shortcut method)
     */
    getWalletAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const metadata = yield this.getUserMetadata();
            return metadata.publicAddress;
        });
    }
    /**
     * Get Web3 provider for signing transactions
     */
    getProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initialized) {
                yield this.init();
            }
            return this.magic.rpcProvider;
        });
    }
    /**
     * Get the Magic instance (for advanced usage)
     */
    getMagicInstance() {
        return this.magic;
    }
}
